import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { SelectButtonModule } from 'primeng/selectbutton';
import { AuthService } from '../../../../../services/auth.service';
import { ProductDbService } from '../../../../../services/productdb.service';
import { DynamicTemplateConfigService } from '../../../../../services/pdf-generation/dynamic-template-config.service';
import { environment } from '../../../../app.environment';
import { User } from '../../../../../modals/user.model';
import { DashboardActionContext, FooterActionConfig } from '../../../../shared/industry-ui.model';
import { BillNumberRefreshService } from '../../../../shared/services/bill-number-refresh.service';
import { Subscription } from 'rxjs';


import { InputSwitchModule } from 'primeng/inputswitch';
import { NumericKeypadDialogComponent } from '../../../../shared/components/numeric-keypad-dialog/numeric-keypad-dialog.component';

/**
 * RestaurantBillPanelComponent
 * ─────────────────────────────────────────────────────────────────────────────
 * Comprehensive "Current Bill" sidebar panel for the Restaurant layout.
 * - Displays live Bill No flip-counter (WebSocket + state) and Counter No.
 * - Shows cart items (max 10 items height, scrollable beyond 10 items).
 * - Full totals breakdown (subtotal, taxes, product discount, overall discount, net total).
 * - Process Order button & Quick Pay / POS Actions Rail.
 * ─────────────────────────────────────────────────────────────────────────────
 */
@Component({
  selector: 'app-restaurant-bill-panel',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    SelectButtonModule,
    InputSwitchModule,
    NumericKeypadDialogComponent,
  ],
  templateUrl: './restaurant-bill-panel.component.html',
  styleUrls: ['./restaurant-bill-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RestaurantBillPanelComponent implements OnInit, OnChanges, OnDestroy {
  /* ── Cart items ── */
  @Input() products: any[] = [];

  /* ── Footer / Mode Actions ── */
  @Input() actions: FooterActionConfig[] = [];
  @Input() context!: DashboardActionContext;

  /* ── Cart Summary Inputs ── */
  @Input() uniqueItemsCount: number = 0;
  @Input() totalQuantity: number = 0;

  /* ── Pre-calculated totals ── */
  @Input() subtotal: number = 0;
  @Input() isInterStateTaxOrder: boolean = false;
  @Input() igst: number = 0;
  @Input() cgst: number = 0;
  @Input() sgst: number = 0;
  @Input() taxAmount: number = 0;
  @Input() productDiscount: number = 0;

  /* ── Overall Discount Inputs ── */
  @Input() enableOverAllDiscount: boolean = false;
  @Input() isExchangeOrder: boolean = false;
  @Input() isUnitPriceInclTax: boolean = false; // Show unit price incl/excl tax
  @Input() overAllDiscountMode: 'percentage' | 'amount' = 'amount';
  @Input() overAllDiscountPercent: number = 0;
  @Input() overAllDiscount: number = 0;
  @Input() discountModeOptions: any[] = [];
  @Input() subtotalAfterLineDiscount: number = 0;
  @Input() overAllDiscountAmount: number = 0;

  /* ── Net Amount Inputs ── */
  @Input() roundOffAmount: number = 0;
  @Input() isMobileIndustry: boolean = false;
  @Input() freeAmount: number = 0;
  @Input() roundedTotal: number = 0;
  @Input() profitAmount: number = 0;
  @Input() currencySymbol: string = '₹';

  /* ── Item & Sales Discount Inputs ── */
  @Input() isSalesDiscountEditEnabled: boolean = true;
  @Input() discountMode: 'percentage' | 'amount' = 'percentage';

  /* ── Mode flags ── */
  @Input() isQuotationMode: boolean = false;
  @Input() hasPendingVariantSelection: boolean = false;
  @Input() isProcessingQuotation: boolean = false;
  @Input() canCompleteQuotation: boolean = false;
  @Input() isOrderBlocked: boolean = false;

  /* ── Customer info ── */
  @Input() customerName: string = '';
  @Input() mobileNumber: string = '';

  /* ── Actions Rail state ── */
  isRailOpen = true;

  /* ── Output Events ── */
  @Output() processOrderClicked = new EventEmitter<void>();
  @Output() cancelBillClicked = new EventEmitter<void>();
  @Output() holdReceiptClicked = new EventEmitter<void>();
  @Output() resumeReceiptClicked = new EventEmitter<void>();
  @Output() increaseQtyClicked = new EventEmitter<any>();
  @Output() decreaseQtyClicked = new EventEmitter<any>();
  @Output() removeItemClicked = new EventEmitter<any>();
  @Output() addCustomerClicked = new EventEmitter<void>();
  @Output() cashPayClicked = new EventEmitter<void>();
  @Output() upiPayClicked = new EventEmitter<void>();
  @Output() cardPayClicked = new EventEmitter<void>();

  @Output() itemDiscountModeChanged = new EventEmitter<any>();
  @Output() discountModeChanged = new EventEmitter<any>();
  @Output() discountValueChanged = new EventEmitter<number>();
  @Output() discountBlurred = new EventEmitter<void>();
  @Output() itemDiscountChanged = new EventEmitter<{ event: any; product: any }>();

  /* ── Expandable Product Discount State ── */
  expandedDiscountItemIds = new Set<any>();

  toggleDiscountExpand(product: any): void {
    const id = product.psid || product.productId || product.id;
    if (this.expandedDiscountItemIds.has(id)) {
      this.expandedDiscountItemIds.delete(id);
    } else {
      this.expandedDiscountItemIds.add(id);
    }
  }

  isDiscountExpanded(product: any): boolean {
    const id = product.psid || product.productId || product.id;
    return this.expandedDiscountItemIds.has(id) || (product.discount > 0);
  }

  onItemDiscountInput(event: Event, product: any): void {
    this.itemDiscountChanged.emit({ event, product });
  }

  toggleItemDiscountMode(): void {
    const nextMode = this.discountMode === 'percentage' ? 'amount' : 'percentage';
    this.itemDiscountModeChanged.emit(nextMode);
  }

  get isAmountMode(): boolean {
    return this.discountMode === 'amount';
  }

  onProductDiscountToggle(mode: 'percentage' | 'amount'): void {
    this.discountMode = mode;
    this.itemDiscountModeChanged.emit(mode);
  }

  onOverAllDiscountModeToggle(mode: 'percentage' | 'amount'): void {
    this.overAllDiscountMode = mode;
    this.discountModeChanged.emit(mode);
  }

  // ─── Touch numeric keypad for discount entry (restaurant layout only) ───
  // TODO: once a "touch screen mode" setting exists (ClientSettingsService),
  // gate keypadEnabled behind it instead of the hardcoded `true` below —
  // every call site already funnels through this one flag.
  get isTouchKeypadEnabled(): boolean {
    return true;
  }

  keypadOpen = false;
  keypadLabel = '';
  keypadSubLabel = '';
  keypadUnit = '';
  keypadInitialValue = 0;
  keypadMaxValue: number | null = null;
  private keypadTarget: 'item' | 'overall' | null = null;
  private keypadItemProduct: any = null;

  openItemDiscountKeypad(product: any): void {
    if (!this.isTouchKeypadEnabled) return;
    this.keypadTarget = 'item';
    this.keypadItemProduct = product;
    this.keypadLabel = 'Item Discount';
    this.keypadSubLabel = product.name;
    this.keypadUnit = this.discountMode === 'percentage' ? '%' : this.currencySymbol;
    this.keypadInitialValue = product.discount || 0;
    this.keypadMaxValue =
      this.discountMode === 'percentage' ? 100 : this.getDisplayPrice(product);
    this.keypadOpen = true;
  }

  openOverallDiscountKeypad(): void {
    if (!this.isTouchKeypadEnabled) return;
    this.keypadTarget = 'overall';
    this.keypadItemProduct = null;
    this.keypadLabel = 'Overall Discount';
    this.keypadSubLabel = '';
    this.keypadUnit = this.overAllDiscountMode === 'percentage' ? '%' : this.currencySymbol;
    this.keypadInitialValue = parseFloat(this.discountRawValue) || 0;
    this.keypadMaxValue =
      this.overAllDiscountMode === 'percentage' ? 100 : this.subtotalAfterLineDiscount;
    this.keypadOpen = true;
  }

  onKeypadConfirm(value: number): void {
    if (this.keypadTarget === 'item' && this.keypadItemProduct) {
      const fakeEvent = { target: { value: String(value) } } as unknown as Event;
      this.itemDiscountChanged.emit({ event: fakeEvent, product: this.keypadItemProduct });
    } else if (this.keypadTarget === 'overall') {
      this.discountRawValue = value === 0 ? '' : String(value);
      if (this.overAllDiscountMode === 'percentage') {
        this.lastDiscountPercent = value;
      } else {
        this.lastDiscountAmount = value;
      }
      this.discountValueChanged.emit(value);
      this.discountBlurred.emit();
    }
    this.keypadTarget = null;
    this.keypadItemProduct = null;
  }

  onKeypadCancel(): void {
    this.keypadTarget = null;
    this.keypadItemProduct = null;
  }

  // ─── Bill Counter State (REST refresh-after-sale + polling) ───
  private demoBillNumber: number = 0;
  /** Subscription to BillNumberRefreshService.billNumber$ (replaces the WebSocket) */
  private billNumberSubscription: Subscription | null = null;
  // WebSocket bill counter disabled — replaced by BillNumberRefreshService.
  // private billNumberSocket: WebSocket | null = null;
  // private billNumberReconnectTimer: ReturnType<typeof setTimeout> | null = null;
  // private isBillNumberSocketClosing = false;

  previousBillDigits: number[] = [];
  isFlipping: boolean[] = [];

  // ─── Overall Discount Input Buffer ───
  discountRawValue: string = '';
  private lastDiscountMode: string = '';
  private lastDiscountPercent: number = 0;
  private lastDiscountAmount: number = 0;

  constructor(
    private authService: AuthService,
    private productService: ProductDbService,
    private dynamicTemplateConfigService: DynamicTemplateConfigService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private billNumberRefreshService: BillNumberRefreshService,
  ) {}

  ngOnInit(): void {
    this.startDemoBillNumberTimer();
  }

  ngOnDestroy(): void {
    // this.disconnectBillNumberSocket();
    this.billNumberSubscription?.unsubscribe();
    this.billNumberSubscription = null;
  }

  ngOnChanges(changes: SimpleChanges): void {
    const current =
      this.overAllDiscountMode === 'percentage'
        ? this.overAllDiscountPercent
        : this.overAllDiscount;
    const modeChanged = this.overAllDiscountMode !== this.lastDiscountMode;
    const valueChanged =
      this.overAllDiscountPercent !== this.lastDiscountPercent ||
      this.overAllDiscount !== this.lastDiscountAmount;

    if (modeChanged || valueChanged) {
      this.discountRawValue = current === 0 ? '' : String(current);
      this.lastDiscountMode = this.overAllDiscountMode;
      this.lastDiscountPercent = this.overAllDiscountPercent;
      this.lastDiscountAmount = this.overAllDiscount;
    }
  }

  onDiscountInput(event: Event): void {
    const raw = (event.target as HTMLInputElement).value;
    this.discountRawValue = raw;

    const parsed = parseFloat(raw);
    const finalValue = isNaN(parsed) || parsed < 0 ? 0 : parsed;

    if (this.overAllDiscountMode === 'percentage') {
      this.lastDiscountPercent = finalValue;
    } else {
      this.lastDiscountAmount = finalValue;
    }

    this.discountValueChanged.emit(finalValue);
  }

  onDiscountBlur(): void {
    const parsed = parseFloat(this.discountRawValue);
    const finalValue = isNaN(parsed) || parsed < 0 ? 0 : parsed;
    this.discountRawValue = finalValue === 0 ? '' : String(finalValue);
    this.discountBlurred.emit();
  }

  toggleRail(): void {
    this.isRailOpen = !this.isRailOpen;
  }

  onActionClick(action: FooterActionConfig): void {
    if (!this.context) return;
    if (action.disabledWhen?.(this.context)) return;
    action.handler(this.context);
  }

  getLabel(action: FooterActionConfig): string {
    return this.context && action.label ? action.label(this.context) : '';
  }

  isDisabled(action: FooterActionConfig): boolean {
    return this.context && action.disabledWhen ? action.disabledWhen(this.context) : false;
  }

  getProcessButtonText(): string {
    if (this.isExchangeOrder) {
      return 'Process Return';
    } else if (this.isQuotationMode) {
      return 'Process Quotation';
    } else {
      return 'Process Order';
    }
  }

  getCounterNumber(): string {
    const counterSession = this.authService.getCounterSession();
    if (counterSession?.billcounter?.name) {
      return counterSession.billcounter.name;
    }
    if (counterSession?.billcounter_id) {
      return counterSession.billcounter_id.toString().padStart(2, '0');
    }
    return '00';
  }

  getBillNumber(): string {
    return this.demoBillNumber.toString() || '0000';
  }

  getBillNumberDigits(): number[] {
    const billNo = this.getBillNumber();
    const paddedBillNo = billNo.length < 4 ? billNo.padStart(4, '0') : billNo;
    return paddedBillNo.split('').map((d) => parseInt(d, 10));
  }

  hasCustomer(): boolean {
    return !!(this.customerName || this.mobileNumber);
  }

  getDisplayPrice(product: any): number {
    if (this.isUnitPriceInclTax) {
      return product.product_selling_price_incl ?? product.sellingpriceincltax ?? product.sellingprice ?? 0;
    }
    return product.sellingprice ?? product.sellingpriceincltax ?? 0;
  }

  getProductQty(product: any): number {
    return product.increaseQuantity ?? product.quantity ?? product.qty ?? 1;
  }

  getLineTotal(product: any): number {
    const price = this.getDisplayPrice(product);
    const qty = this.getProductQty(product);
    return price * qty;
  }

  // ─── Live Bill Counter (REST refresh-after-sale + polling) ───
  private startDemoBillNumberTimer(): void {
    const initialDigits = this.getBillNumberDigits();
    this.previousBillDigits = [...initialDigits];
    this.isFlipping = new Array(initialDigits.length).fill(false);

    // this.fetchLatestBillNo();
    // this.connectBillNumberSocket();
    this.billNumberSubscription?.unsubscribe();
    this.billNumberSubscription = this.billNumberRefreshService.billNumber$
      .subscribe((billNumber) => this.updateDisplayedBillNumber(billNumber));
  }

  // ─── Previous WebSocket implementation (disabled, kept for reference) ───
  // private fetchLatestBillNo(): void {
    // this.productService.getLatestBillNo().subscribe({
      // next: (response) => {
        // if (response?.data?.count !== undefined) {
          // this.updateDisplayedBillNumber(response.data.count);
        // }
      // },
      // error: (error) => {
        // console.error('Failed to fetch latest bill number:', error);
      // },
    // });
  // }

  // private connectBillNumberSocket(): void {
    // const token = this.getBillNumberAccessToken();
    // const wsUrl = this.getBillNumberWebSocketUrl();

    // if (!token || !wsUrl || this.billNumberSocket) {
      // return;
    // }

    // this.isBillNumberSocketClosing = false;
    // this.billNumberSocket = new WebSocket(wsUrl);

    // this.billNumberSocket.onopen = () => {
      // const connectFrame = [
        // 'CONNECT',
        // 'accept-version:1.2',
        // 'heart-beat:0,0',
        // `login:${token}`,
        // '',
        // '\0',
      // ].join('\n');

      // this.billNumberSocket?.send(connectFrame);
    // };

    // this.billNumberSocket.onmessage = (event) => {
      // const frames = String(event.data)
        // .split('\0')
        // .map((frame) => frame.trim())
        // .filter(Boolean);

      // frames.forEach((frame) => this.handleBillNumberSocketFrame(frame));
    // };

    // this.billNumberSocket.onclose = () => {
      // this.billNumberSocket = null;
      // if (!this.isBillNumberSocketClosing) {
        // this.billNumberReconnectTimer = setTimeout(() => {
          // this.connectBillNumberSocket();
        // }, 5000);
      // }
    // };

    // this.billNumberSocket.onerror = (error) => {
      // console.error('Bill number websocket error:', error);
    // };
  // }

  // private handleBillNumberSocketFrame(frame: string): void {
    // const [commandLine, ...rest] = frame.split('\n');
    // const command = commandLine.trim();
    // const emptyLineIndex = rest.findIndex((line) => line.trim() === '');
    // const headerLines = emptyLineIndex >= 0 ? rest.slice(0, emptyLineIndex) : rest;
    // const bodyLines = emptyLineIndex >= 0 ? rest.slice(emptyLineIndex + 1) : [];

    // if (command === 'CONNECTED') {
      // this.subscribeToBillNumberTopics();
      // return;
    // }

    // if (command !== 'MESSAGE') {
      // return;
    // }

    // const headers = headerLines.reduce(
      // (acc, line) => {
        // const separatorIndex = line.indexOf(':');
        // if (separatorIndex > -1) {
          // const key = line.slice(0, separatorIndex).trim();
          // const value = line.slice(separatorIndex + 1).trim();
          // acc[key] = value;
        // }
        // return acc;
      // },
      // {} as Record<string, string>,
    // );

    // const subscription = headers['subscription'];
    // const body = bodyLines.join('\n').trim();

    // if (subscription && body) {
      // this.applyBillNumberUpdate(body);
    // }
  // }

  // private subscribeToBillNumberTopics(): void {
    // const topics = this.getBillNumberTopics();

    // topics.forEach((topic, index) => {
      // const subscribeFrame = [
        // 'SUBSCRIBE',
        // `id:bill-number-${index}`,
        // `destination:${topic}`,
        // '',
        // '\0',
      // ].join('\n');

      // this.billNumberSocket?.send(subscribeFrame);
    // });
  // }

  // private applyBillNumberUpdate(messageBody: string): void {
    // try {
      // const payload = JSON.parse(messageBody);
      // const parsedBillNumber = this.extractNumericBillNumber(
        // payload?.billNo,
        // payload?.billno,
        // payload?.billNumber,
        // payload?.invoiceNo,
        // payload?.invoiceno,
        // payload?.invoiceinc,
        // payload?.count,
      // );

      // if (parsedBillNumber === null) {
        // return;
      // }
      // this.updateDisplayedBillNumber(parsedBillNumber);
    // } catch (error) {
      // console.error('Failed to parse bill number websocket payload:', error);
    // }
  // }

  private updateDisplayedBillNumber(parsedBillNumber: number): void {
    this.ngZone.run(() => {
      const oldDigits = this.getBillNumberDigits();
      this.demoBillNumber = parsedBillNumber;
      const newDigits = this.getBillNumberDigits();

      if (newDigits.length > this.isFlipping.length) {
        this.isFlipping = new Array(newDigits.length).fill(false);
        this.previousBillDigits = [...oldDigits];
      }

      newDigits.forEach((digit, index) => {
        if (digit !== oldDigits[index]) {
          this.isFlipping[index] = true;
          setTimeout(() => {
            this.isFlipping[index] = false;
            this.previousBillDigits[index] = digit;
            this.cdr.markForCheck();
          }, 500);
        }
      });

      this.cdr.markForCheck();
    });
  }

  // private getBillNumberTopics(): string[] {
    // const topicContext = this.getBillNumberTopicContext();
    // if (!topicContext) return [];
    // return [`/topic/sales-count/${topicContext.clientId}/${topicContext.storeId}`];
  // }

  // private getBillNumberWebSocketUrl(): string {
    // const apiBaseUrl = environment.apiURL.replace(/\/api\/?$/, '');
    // if (apiBaseUrl.startsWith('https://')) {
      // return `${apiBaseUrl.replace('https://', 'wss://')}/ws`;
    // }
    // if (apiBaseUrl.startsWith('http://')) {
      // return `${apiBaseUrl.replace('http://', 'ws://')}/ws`;
    // }
    // return '';
  // }

  // private getBillNumberAccessToken(): string {
    // return this.getBillNumberUser()?.accessToken || '';
  // }

  // private getBillNumberUser(): User | null {
    // const currentUser = this.authService.currentUserValue;
    // if (currentUser?.accessToken) {
      // return currentUser;
    // }
    // try {
      // const storedUser = localStorage.getItem('name');
      // return storedUser ? (JSON.parse(storedUser) as User) : null;
    // } catch {
      // return null;
    // }
  // }

  // private getBillNumberTopicContext(): { clientId: string; storeId: string } | null {
    // const user = this.getBillNumberUser();
    // const decodedToken = this.decodeBillNumberToken(user?.accessToken);
    // const clientId = this.normalizeBillNumberTopicSegment(
      // user?.client_id ?? decodedToken?.['client_id'],
    // );
    // const storeId = this.normalizeBillNumberTopicSegment(
      // user?.store_id ?? decodedToken?.['store'] ?? decodedToken?.['store_id'],
    // );

    // if (!clientId || !storeId) return null;
    // return { clientId, storeId };
  // }

  // private normalizeBillNumberTopicSegment(value: unknown): string | null {
    // if (value === undefined || value === null) return null;
    // const normalizedValue = String(value).trim();
    // return normalizedValue ? normalizedValue : null;
  // }

  // private decodeBillNumberToken(token?: string): Record<string, any> | null {
    // if (!token) return null;
    // try {
      // const payload = token.split('.')[1];
      // const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
      // const decodedPayload = atob(normalizedPayload);
      // return JSON.parse(decodedPayload);
    // } catch {
      // return null;
    // }
  // }

  // private extractNumericBillNumber(...candidates: unknown[]): number | null {
    // return candidates.reduce<number | null>((matchedValue, candidate) => {
      // if (
        // matchedValue !== null ||
        // candidate === undefined ||
        // candidate === null ||
        // candidate === ''
      // ) {
        // return matchedValue;
      // }
      // const numericCandidate = Number(candidate);
      // return Number.isNaN(numericCandidate) ? null : numericCandidate;
    // }, null);
  // }

  // private disconnectBillNumberSocket(): void {
    // this.isBillNumberSocketClosing = true;
    // if (this.billNumberReconnectTimer) {
      // clearTimeout(this.billNumberReconnectTimer);
      // this.billNumberReconnectTimer = null;
    // }
    // if (this.billNumberSocket) {
      // if (this.billNumberSocket.readyState === WebSocket.OPEN) {
        // this.billNumberSocket.send(['DISCONNECT', '', '\0'].join('\n'));
      // }
      // this.billNumberSocket.close();
      // this.billNumberSocket = null;
    // }
  // }
}

